/**
 * ============================================================================
 * KbBlockActionMenu — Menu contextual do botão "⋮⋮" do handle lateral
 *                     (estilo GitBook). Cada tipo de bloco expõe seu próprio
 *                     conjunto de ações: Turn into, Duplicate, Delete, Move
 *                     up/down + ações específicas (tabela, callout, cards,
 *                     steps, toggle, imagem, embed, heading).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.1.0 - Preserva scroll nas ações do menu e bloqueia
 *            autofocus do Radix para evitar salto da tela ao final do artigo.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Bloco Handle GitBook).
 * ============================================================================
 */
import type { Editor } from "@tiptap/react";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Fragment } from "@tiptap/pm/model";
import {
  DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import {
  Copy, Trash2, ArrowUp, ArrowDown, Repeat,
  Heading1, Heading2, Heading3, Type, Quote as QuoteIcon, List,
  ListOrdered, ListChecks, Code2, AlertTriangle,
  Columns3, Rows3, Merge, Split, TableProperties,
  LayoutGrid, Plus, AlignLeft, AlignCenter, AlignRight,
} from "lucide-react";

interface Props {
  editor: Editor;
  node: PMNode;
  /** Posição de início do bloco no documento (antes do node). */
  pos: number;
  onClose: () => void;
}

// -------------------------------------------------------------------------
// Helpers de manipulação de nó por posição (independente da seleção)
// -------------------------------------------------------------------------

/** Retorna [from, to] do nó começando em `pos`. */
function nodeRange(node: PMNode, pos: number): [number, number] {
  return [pos, pos + node.nodeSize];
}

/** Duplica o nó logo abaixo. */
function duplicateNode(editor: Editor, node: PMNode, pos: number) {
  const [, to] = nodeRange(node, pos);
  editor.view.dispatch(editor.state.tr.insert(to, node.copy(node.content)));
}

/** Deleta o nó inteiro. */
function deleteNode(editor: Editor, node: PMNode, pos: number) {
  const [from, to] = nodeRange(node, pos);
  editor.view.dispatch(editor.state.tr.delete(from, to));
}

/** Move o nó para cima trocando com o irmão anterior. */
function moveNodeUp(editor: Editor, node: PMNode, pos: number) {
  const { state } = editor.view;
  const $pos = state.doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  if (index === 0) return;
  const prev = parent.child(index - 1);
  const from = pos - prev.nodeSize;
  const to = pos + node.nodeSize;
  const tr = state.tr.replaceWith(from, to, Fragment.fromArray([node, prev]));
  editor.view.dispatch(tr);
}

/** Move o nó para baixo trocando com o irmão seguinte. */
function moveNodeDown(editor: Editor, node: PMNode, pos: number) {
  const { state } = editor.view;
  const $pos = state.doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  if (index >= parent.childCount - 1) return;
  const next = parent.child(index + 1);
  const from = pos;
  const to = pos + node.nodeSize + next.nodeSize;
  const tr = state.tr.replaceWith(from, to, Fragment.fromArray([next, node]));
  editor.view.dispatch(tr);
}

/** Foca o editor DENTRO do nó em `pos` (útil antes de rodar comandos que dependem da seleção). */
function selectInside(editor: Editor, node: PMNode, pos: number) {
  const inside = pos + 1;
  const to = Math.min(inside, pos + node.nodeSize - 1);
  editor.chain().focus().setTextSelection(to).run();
}

/**
 * Foca dentro da PRIMEIRA CÉLULA da tabela.
 * Necessário porque comandos do prosemirror-tables (addColumnBefore, etc.)
 * exigem isInTable(state) — precisam de seleção dentro de uma célula, não
 * apenas dentro do nó tabela.
 */
function selectInsideTable(editor: Editor, tableNode: PMNode, tablePos: number) {
  // tablePos = posição do nó tabela; +1 entra na tabela; +1 na tableRow; +1 na célula; +1 no parágrafo
  const cellTextPos = tablePos + 4;
  const maxPos = tablePos + tableNode.nodeSize - 1;
  const target = Math.min(cellTextPos, maxPos);
  editor.chain().focus().setTextSelection(target).run();
}

/**
 * Garante que a seleção esteja dentro da tabela. Se o usuário já tem cursor
 * numa célula da tabela (isInTable), preserva; caso contrário, foca na
 * primeira célula. Assim ações do menu 6-pontinhos respeitam o cursor.
 */
function ensureInsideTable(editor: Editor, tableNode: PMNode, tablePos: number) {
  const { selection } = editor.state;
  const from = selection.from;
  const tableFrom = tablePos;
  const tableTo = tablePos + tableNode.nodeSize;
  if (from > tableFrom && from < tableTo) {
    // Seleção já está dentro dessa tabela — mantém
    editor.view.focus();
    return;
  }
  selectInsideTable(editor, tableNode, tablePos);
}

/** Captura a rolagem externa do editor KB antes de ações que podem chamar focus(). */
function captureEditorScroll(editor: Editor) {
  const scrollEl = editor.view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
  return {
    scrollEl,
    top: scrollEl?.scrollTop ?? 0,
    left: scrollEl?.scrollLeft ?? 0,
  };
}

/** Restaura a rolagem em múltiplos frames para vencer autofocus do Radix/Tiptap. */
function restoreEditorScroll(snapshot: ReturnType<typeof captureEditorScroll>) {
  const restore = () => {
    if (!snapshot.scrollEl) return;
    snapshot.scrollEl.scrollTop = snapshot.top;
    snapshot.scrollEl.scrollLeft = snapshot.left;
  };
  restore();
  window.requestAnimationFrame(() => {
    restore();
    window.requestAnimationFrame(restore);
  });
}

/** Executa uma ação de menu sem permitir que a tela pule para o fim do artigo. */
function preserveEditorScroll(editor: Editor, action: () => void) {
  const snapshot = captureEditorScroll(editor);
  action();
  restoreEditorScroll(snapshot);
}

/** Atualiza atributos por posição sem focar o editor desnecessariamente. */
function setNodeAttrsAt(editor: Editor, pos: number, attrs: Record<string, unknown>) {
  let tr = editor.state.tr;
  Object.entries(attrs).forEach(([key, value]) => {
    tr = tr.setNodeAttribute(pos, key, value);
  });
  editor.view.dispatch(tr);
}

// -------------------------------------------------------------------------
// Componente
// -------------------------------------------------------------------------
export function KbBlockActionMenu({ editor, node, pos, onClose }: Props) {
  const type = node.type.name;

  // Detecta se o bloco está DENTRO de um `stepItem` (passo). Se sim, expõe
  // ações para manipular o passo inteiro (inserir passo acima/abaixo,
  // duplicar/excluir passo, mover passo). Isso é essencial porque o handle
  // lateral, dentro de um passo, se ancora no bloco filho — sem essas ações
  // não haveria como reordenar/excluir o passo inteiro.
  const $pos = editor.state.doc.resolve(pos);
  const parent = $pos.depth > 0 ? $pos.node($pos.depth) : null;
  const inStepItem = parent?.type.name === "stepItem";
  const stepItemPos = inStepItem ? $pos.before($pos.depth) : null;
  const stepItemNode = stepItemPos != null ? editor.state.doc.nodeAt(stepItemPos) : null;

  // Wrapper para item de menu que executa comando e fecha
  const run = (fn: () => void) => () => {
    preserveEditorScroll(editor, () => {
      fn();
      onClose();
    });
  };

  return (
    <DropdownMenuContent
      align="start"
      side="bottom"
      sideOffset={4}
      data-kb-editor-ui="true"
      onCloseAutoFocus={(event) => {
        // Não devolver foco ao trigger do handle. Em Tiptap/ProseMirror isso
        // reposiciona seleção e pode rolar a coluna direita até o final.
        event.preventDefault();
      }}
      className="w-64"
    >
      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {labelForType(type)}
      </DropdownMenuLabel>


      {/* -------- Turn into (para blocos textuais) -------- */}
      {canTurnInto(type) && (
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Repeat className="h-4 w-4 mr-2" /> Transformar em
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56">
            <TurnIntoItem editor={editor} pos={pos} node={node} to="paragraph" label="Parágrafo" icon={Type} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="h1" label="Título 1" icon={Heading1} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="h2" label="Título 2" icon={Heading2} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="h3" label="Título 3" icon={Heading3} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="quote" label="Citação" icon={QuoteIcon} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="bulletList" label="Lista" icon={List} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="orderedList" label="Lista numerada" icon={ListOrdered} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="taskList" label="Checklist" icon={ListChecks} onDone={onClose} />
            <TurnIntoItem editor={editor} pos={pos} node={node} to="codeBlock" label="Bloco de código" icon={Code2} onDone={onClose} />
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      )}

      {/* -------- Ações específicas: PASSO (quando o bloco está dentro de um stepItem) -------- */}
      {inStepItem && stepItemPos != null && stepItemNode && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Passo
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={run(() => {
              editor.chain()
                .insertContentAt(stepItemPos, { type: "stepItem", content: [{ type: "paragraph" }] })
                .setTextSelection(stepItemPos + 2)
                .focus()
                .run();
            })}
          >
            <ArrowUp className="h-4 w-4 mr-2" /> Inserir passo acima
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={run(() => {
              const insertAt = stepItemPos + stepItemNode.nodeSize;
              editor.chain()
                .insertContentAt(insertAt, { type: "stepItem", content: [{ type: "paragraph" }] })
                .setTextSelection(insertAt + 2)
                .focus()
                .run();
            })}
          >
            <ArrowDown className="h-4 w-4 mr-2" /> Inserir passo abaixo
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => moveNodeUp(editor, stepItemNode, stepItemPos))}>
            <ArrowUp className="h-4 w-4 mr-2" /> Mover passo para cima
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => moveNodeDown(editor, stepItemNode, stepItemPos))}>
            <ArrowDown className="h-4 w-4 mr-2" /> Mover passo para baixo
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => duplicateNode(editor, stepItemNode, stepItemPos))}>
            <Copy className="h-4 w-4 mr-2" /> Duplicar passo
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={run(() => deleteNode(editor, stepItemNode, stepItemPos))}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Excluir passo
          </DropdownMenuItem>
        </>
      )}


      {/* -------- Ações específicas: TABELA (fallback do menu 6-pontinhos) --------
          O caminho principal para operar por coluna/linha são os HANDLES INLINE
          renderizados pelo TableNodeView (respeitam a coluna/linha sob o mouse).
          Estas ações aqui operam a partir da seleção atual do editor — se o
          usuário estiver com o cursor dentro de uma célula, valem para essa
          célula; caso contrário, caem para a PRIMEIRA célula da tabela.
      */}
      {type === "table" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().addColumnBefore().run(); })}>
            <Columns3 className="h-4 w-4 mr-2" /> Inserir coluna à esquerda
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().addColumnAfter().run(); })}>
            <Columns3 className="h-4 w-4 mr-2" /> Inserir coluna à direita
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().addRowBefore().run(); })}>
            <Rows3 className="h-4 w-4 mr-2" /> Inserir linha acima
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().addRowAfter().run(); })}>
            <Rows3 className="h-4 w-4 mr-2" /> Inserir linha abaixo
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().toggleHeaderRow().run(); })}>
            <TableProperties className="h-4 w-4 mr-2" /> Alternar cabeçalho da linha
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().toggleHeaderColumn().run(); })}>
            <TableProperties className="h-4 w-4 mr-2" /> Alternar cabeçalho da coluna
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().mergeCells().run(); })}>
            <Merge className="h-4 w-4 mr-2" /> Mesclar células
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().splitCell().run(); })}>
            <Split className="h-4 w-4 mr-2" /> Dividir célula
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().deleteRow().run(); })} className="text-destructive focus:text-destructive">
            <Trash2 className="h-4 w-4 mr-2" /> Remover linha
          </DropdownMenuItem>
          <DropdownMenuItem onClick={run(() => { ensureInsideTable(editor, node, pos); editor.chain().focus().deleteColumn().run(); })} className="text-destructive focus:text-destructive">
            <Trash2 className="h-4 w-4 mr-2" /> Remover coluna
          </DropdownMenuItem>
        </>
      )}


      {type === "callout" && (
        <>
          <DropdownMenuSeparator />
          {(["info", "success", "warning", "danger"] as const).map((v) => (
            <DropdownMenuItem
              key={v}
              onClick={run(() => {
                setNodeAttrsAt(editor, pos, { variant: v });
              })}
            >
              <AlertTriangle className="h-4 w-4 mr-2" /> Variante: {v}
            </DropdownMenuItem>
          ))}
        </>
      )}

      {type === "cardGrid" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={run(() => {
              const end = pos + node.nodeSize - 1;
              editor.chain().insertContentAt(end, {
                type: "card",
                attrs: { title: "Novo cartão", description: "Descrição…", href: "#" },
              }).run();
            })}
          >
            <Plus className="h-4 w-4 mr-2" /> Adicionar cartão
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger><LayoutGrid className="h-4 w-4 mr-2" /> Colunas</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {[2, 3, 4].map((n) => (
                <DropdownMenuItem
                  key={n}
                  onClick={run(() => {
                    setNodeAttrsAt(editor, pos, { columns: n });
                  })}
                >
                  {n} colunas
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </>
      )}

      {type === "steps" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={run(() => {
              const end = pos + node.nodeSize - 1;
              editor.chain().insertContentAt(end, {
                type: "stepItem",
                content: [{ type: "paragraph" }],
              }).run();
            })}
          >
            <Plus className="h-4 w-4 mr-2" /> Adicionar passo
          </DropdownMenuItem>
        </>
      )}

      {type === "toggle" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={run(() => {
              const current = String(node.attrs.summary ?? "");
              const s = window.prompt("Título do bloco:", current) ?? current;
              setNodeAttrsAt(editor, pos, { summary: s });
            })}
          >
            Renomear título
          </DropdownMenuItem>
        </>
      )}

      {type === "image" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Alinhamento</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem onClick={run(() => { setNodeAttrsAt(editor, pos, { align: "left" }); })}>
                <AlignLeft className="h-4 w-4 mr-2" /> Esquerda
              </DropdownMenuItem>
              <DropdownMenuItem onClick={run(() => { setNodeAttrsAt(editor, pos, { align: "center" }); })}>
                <AlignCenter className="h-4 w-4 mr-2" /> Centro
              </DropdownMenuItem>
              <DropdownMenuItem onClick={run(() => { setNodeAttrsAt(editor, pos, { align: "right" }); })}>
                <AlignRight className="h-4 w-4 mr-2" /> Direita
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Tamanho</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {(["sm", "md", "lg", "full"] as const).map((s) => (
                <DropdownMenuItem
                  key={s}
                  onClick={run(() => { setNodeAttrsAt(editor, pos, { size: s }); })}
                >
                  {s}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            onClick={run(() => {
              const current = String(node.attrs.alt ?? "");
              const alt = window.prompt("Texto alternativo (alt):", current) ?? current;
              setNodeAttrsAt(editor, pos, { alt });
            })}
          >
            Alt text
          </DropdownMenuItem>
        </>
      )}

      {type === "embed" && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={run(() => {
              const current = String(node.attrs.src ?? "");
              const src = window.prompt("URL do embed:", current) ?? current;
              setNodeAttrsAt(editor, pos, { src });
            })}
          >
            Trocar URL
          </DropdownMenuItem>
        </>
      )}

      {type === "heading" && (
        <>
          <DropdownMenuSeparator />
          {[1, 2, 3].map((lvl) => (
            <DropdownMenuItem
              key={lvl}
              onClick={run(() => {
                setNodeAttrsAt(editor, pos, { level: lvl });
              })}
            >
              Nível {lvl}
            </DropdownMenuItem>
          ))}
        </>
      )}

      {/* -------- Ações comuns -------- */}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={run(() => moveNodeUp(editor, node, pos))}>
        <ArrowUp className="h-4 w-4 mr-2" /> Mover para cima
      </DropdownMenuItem>
      <DropdownMenuItem onClick={run(() => moveNodeDown(editor, node, pos))}>
        <ArrowDown className="h-4 w-4 mr-2" /> Mover para baixo
      </DropdownMenuItem>
      <DropdownMenuItem onClick={run(() => duplicateNode(editor, node, pos))}>
        <Copy className="h-4 w-4 mr-2" /> Duplicar
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onClick={run(() => deleteNode(editor, node, pos))}
        className="text-destructive focus:text-destructive"
      >
        <Trash2 className="h-4 w-4 mr-2" /> Excluir
      </DropdownMenuItem>
    </DropdownMenuContent>
  );
}

// -------------------------------------------------------------------------
// Turn into: converte o bloco preservando conteúdo textual quando possível
// -------------------------------------------------------------------------
function TurnIntoItem({
  editor, pos, node, to, label, icon: Icon, onDone,
}: {
  editor: Editor;
  pos: number;
  node: PMNode;
  to: "paragraph" | "h1" | "h2" | "h3" | "quote" | "bulletList" | "orderedList" | "taskList" | "codeBlock";
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onDone: () => void;
}) {
  const active =
    (to === "paragraph" && node.type.name === "paragraph") ||
    (to === "h1" && node.type.name === "heading" && node.attrs.level === 1) ||
    (to === "h2" && node.type.name === "heading" && node.attrs.level === 2) ||
    (to === "h3" && node.type.name === "heading" && node.attrs.level === 3) ||
    (to === "quote" && node.type.name === "blockquote") ||
    (to === "codeBlock" && node.type.name === "codeBlock");

  const onClick = () => {
    preserveEditorScroll(editor, () => {
      // Coloca a seleção DENTRO do bloco (posição pos+1) para que os comandos
      // setNode / toggle* operem no nó correto.
      const inside = pos + 1;
      editor.chain().focus().setTextSelection(inside).run();
      const c = editor.chain().focus();
      switch (to) {
        case "paragraph": c.setNode("paragraph").run(); break;
        case "h1": c.setNode("heading", { level: 1 }).run(); break;
        case "h2": c.setNode("heading", { level: 2 }).run(); break;
        case "h3": c.setNode("heading", { level: 3 }).run(); break;
        case "quote": c.toggleBlockquote().run(); break;
        case "bulletList": c.toggleBulletList().run(); break;
        case "orderedList": c.toggleOrderedList().run(); break;
        case "taskList": c.toggleTaskList().run(); break;
        case "codeBlock": c.toggleCodeBlock().run(); break;
      }
      onDone();
    });
  };

  return (
    <DropdownMenuItem onClick={onClick} className={active ? "bg-accent" : ""}>
      <Icon className="h-4 w-4 mr-2" /> {label}
    </DropdownMenuItem>
  );
}

// -------------------------------------------------------------------------
// Utilitários
// -------------------------------------------------------------------------
function canTurnInto(type: string): boolean {
  return [
    "paragraph", "heading", "blockquote", "bulletList", "orderedList",
    "taskList", "codeBlock",
  ].includes(type);
}

function labelForType(type: string): string {
  const map: Record<string, string> = {
    paragraph: "Parágrafo",
    heading: "Título",
    blockquote: "Citação",
    bulletList: "Lista",
    orderedList: "Lista numerada",
    taskList: "Checklist",
    codeBlock: "Bloco de código",
    horizontalRule: "Divisor",
    table: "Tabela",
    callout: "Destaque",
    steps: "Passos",
    stepItem: "Passo",
    cardGrid: "Cards",
    card: "Cartão",
    toggle: "Expansível",
    image: "Imagem",
    embed: "Embed",
  };
  return map[type] ?? type;
}
